import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { IMG } from "@/lib/images";
import { PartnerButtons, ComplianceNote, StarRating } from "@/components/kit";
import { ChevronLeft, ChevronRight, Sparkles, Wine, ShoppingBag, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const OCCASIONS = ["Wedding", "Birthday", "Anniversary", "Beach Party", "Holiday Gathering", "Housewarming", "Bachelor / Bachelorette", "Backyard Dinner Party"];
const PREFS = ["Cocktails", "Wine", "Beer", "Zero-proof", "Bubbly", "Neat sipping"];
const SPIRITS = ["Tequila", "Bourbon", "Whiskey", "Vodka", "Gin", "Rum", "Champagne", "Red Wine", "White Wine"];
const STYLES = [{ k: "budget", t: "Budget", d: "Smart value picks" }, { k: "balanced", t: "Balanced", d: "The sweet spot" }, { k: "premium", t: "Premium", d: "Top shelf only" }];
const STEPS = ["Occasion", "Guests", "Preferences", "Spirits", "Style", "Budget", "Fulfillment", "Your Bar"];

export default function BuildBar() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [state, setState] = useState({ occasion: "Wedding", guests: 12, preferences: [], spirits: [], style: "balanced", budget: 400, fulfillment: "delivery" });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const toggle = (key, v) => setState((s) => ({ ...s, [key]: s[key].includes(v) ? s[key].filter((x) => x !== v) : [...s[key], v] }));
  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const build = async () => {
    if (!user) { toast.error("Sign in to build your bar"); nav("/login"); return; }
    setLoading(true); setStep(7);
    try { const { data } = await api.post("/build-bar", state); setResult(data); }
    catch { toast.error("Could not build bar"); } finally { setLoading(false); }
  };

  return (
    <div className="grid min-h-[calc(100vh-57px)] lg:grid-cols-[minmax(0,1fr)_400px]">
      <div className="px-6 py-8 lg:px-12">
        {/* stepper */}
        <div className="mb-8 flex flex-wrap items-center gap-1.5">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-1.5">
              <span className={`text-xs font-semibold ${i === step ? "text-primary" : i < step ? "text-foreground" : "text-muted-foreground"}`}>{s}</span>
              {i < STEPS.length - 1 && <span className={`h-px w-6 ${i < step ? "bg-primary" : "bg-border"}`} />}
            </div>
          ))}
        </div>

        <div className="max-w-2xl">
          {step === 0 && <Step title="What's the occasion?" sub="We'll tailor the bar to the moment.">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">{OCCASIONS.map((o) => <Pick key={o} active={state.occasion === o} onClick={() => setState({ ...state, occasion: o })} label={o} testid={`occasion-${o}`} />)}</div>
          </Step>}
          {step === 1 && <Step title="How many guests?" sub="Drives quantities and totals.">
            <div className="flex items-center gap-6"><input data-testid="guest-slider" type="range" min="2" max="120" value={state.guests} onChange={(e) => setState({ ...state, guests: +e.target.value })} className="flex-1 accent-[hsl(var(--primary))]" /><span className="font-display text-5xl text-primary">{state.guests}</span></div>
          </Step>}
          {step === 2 && <Step title="Drink preferences" sub="Pick a few (or none).">
            <div className="flex flex-wrap gap-2">{PREFS.map((p) => <Pill key={p} active={state.preferences.includes(p)} onClick={() => toggle("preferences", p)} label={p} testid={`pref-${p}`} />)}</div>
          </Step>}
          {step === 3 && <Step title="Spirit categories" sub="Which bottles should we prioritize?">
            <div className="flex flex-wrap gap-2">{SPIRITS.map((p) => <Pill key={p} active={state.spirits.includes(p)} onClick={() => toggle("spirits", p)} label={p} testid={`spirit-${p}`} />)}</div>
          </Step>}
          {step === 4 && <Step title="Style level" sub="Set the tone of the shelf.">
            <div className="grid gap-3 sm:grid-cols-3">{STYLES.map((s) => <button key={s.k} data-testid={`style-${s.k}`} onClick={() => setState({ ...state, style: s.k })} className={`rounded-xl border p-5 text-left ${state.style === s.k ? "border-primary bg-primary/10" : "border-border hover:border-primary/40"}`}><p className="font-display text-2xl">{s.t}</p><p className="text-sm text-muted-foreground">{s.d}</p></button>)}</div>
          </Step>}
          {step === 5 && <Step title="Set your budget" sub="We'll build within range.">
            <div className="flex items-center gap-6"><span className="font-display text-4xl text-primary">$</span><input data-testid="budget-slider" type="range" min="100" max="2000" step="50" value={state.budget} onChange={(e) => setState({ ...state, budget: +e.target.value })} className="flex-1 accent-[hsl(var(--primary))]" /><span className="font-display text-5xl text-primary">{state.budget}</span></div>
          </Step>}
          {step === 6 && <Step title="Delivery or pickup?" sub="Fulfilled by our licensed partners.">
            <div className="grid grid-cols-2 gap-3">{["delivery", "pickup"].map((f) => <Pick key={f} active={state.fulfillment === f} onClick={() => setState({ ...state, fulfillment: f })} label={f[0].toUpperCase() + f.slice(1)} testid={`fulfill-${f}`} />)}</div>
            <div className="mt-6"><ComplianceNote /></div>
          </Step>}

          {step === 7 && (
            <div>
              {loading ? (
                <div className="flex flex-col items-center py-20 text-center"><Sparkles className="mb-4 animate-pulse text-primary" size={40} /><p className="font-display text-2xl">Building your bar…</p><p className="text-sm text-muted-foreground">Our concierge is curating bottles for {state.guests} guests.</p></div>
              ) : result && (
                <div className="animate-fade-up">
                  <span className="overline text-primary">{result.ai ? "AI-curated" : "Curated"} for your {state.occasion.toLowerCase()}</span>
                  <h2 className="mt-2 font-display text-4xl">Your recommended bar</h2>
                  <p className="mt-2 text-muted-foreground">{result.summary}</p>
                  {result.host_tip && <p className="mt-3 rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm"><Sparkles size={14} className="mr-1 inline text-primary" />{result.host_tip}</p>}
                  <BarGroup title="Bottles" items={result.bottles} />
                  <BarGroup title="Mixers & modifiers" items={result.mixers} />
                  <BarGroup title="Tools & glassware" items={result.tools} />
                  {result.garnishes?.length > 0 && <div className="mt-6"><p className="mb-2 font-semibold">Garnishes</p><div className="flex flex-wrap gap-2">{result.garnishes.map((g) => <span key={g} className="rounded-full bg-muted px-3 py-1 text-sm">{g}</span>)}</div></div>}
                  <button data-testid="rebuild-btn" onClick={() => { setStep(0); setResult(null); }} className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-primary hover-underline"><RefreshCw size={14} /> Start over</button>
                </div>
              )}
            </div>
          )}

          {step < 7 && (
            <div className="mt-10 flex items-center justify-between">
              <button onClick={back} disabled={step === 0} className="inline-flex items-center gap-1 text-sm font-semibold text-muted-foreground disabled:opacity-30"><ChevronLeft size={16} /> Back</button>
              {step === 6 ? (
                <button data-testid="build-bar-submit" onClick={build} className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 font-semibold text-primary-foreground"><Wine size={16} /> Build my bar</button>
              ) : (
                <button data-testid="wizard-next" onClick={next} className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 font-semibold text-primary-foreground">Next <ChevronRight size={16} /></button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* editorial side + totals */}
      <aside className="relative hidden lg:block">
        <img src={IMG.bar2} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/40" />
        <div className="absolute bottom-0 p-8 text-white">
          {result ? (
            <>
              <p className="overline text-primary">Estimated total</p>
              <p className="font-display text-6xl">${result.estimated_total}</p>
              <p className="mt-1 text-sm text-white/70">for {state.guests} guests · {state.fulfillment}</p>
              <button data-testid="checkout-partners-btn" onClick={() => toast.success("Routing to partners", { description: "Complete delivery or pickup at checkout." })} className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 font-semibold text-primary-foreground"><ShoppingBag size={16} /> Shop this bar</button>
            </>
          ) : (
            <>
              <p className="overline text-primary">Budget bar builder</p>
              <p className="mt-2 font-display text-4xl leading-tight">Pick your drinks,<br />we'll provide the ingredients.</p>
            </>
          )}
        </div>
      </aside>
    </div>
  );
}

function Step({ title, sub, children }) {
  return <div className="animate-fade-up"><h2 className="font-display text-4xl">{title}</h2><p className="mb-6 mt-1 text-muted-foreground">{sub}</p>{children}</div>;
}
function Pick({ active, onClick, label, testid }) {
  return <button data-testid={testid} onClick={onClick} className={`rounded-xl border p-4 text-left text-sm font-medium ${active ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary/40"}`}>{label}</button>;
}
function Pill({ active, onClick, label, testid }) {
  return <button data-testid={testid} onClick={onClick} className={`rounded-full border px-4 py-2 text-sm font-medium ${active ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary/40"}`}>{label}</button>;
}
function BarGroup({ title, items }) {
  if (!items?.length) return null;
  return (
    <div className="mt-6">
      <p className="mb-3 font-semibold">{title}</p>
      <div className="space-y-3">
        {items.map((p) => (
          <div key={p.id} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
            <img src={p.image} alt="" className="h-14 w-14 rounded-lg object-cover" />
            <div className="min-w-0 flex-1"><p className="truncate text-sm font-semibold">{p.name}</p><div className="flex items-center gap-2"><StarRating value={p.rating} size={11} /><span className="text-xs text-muted-foreground">{p.price_band}</span></div></div>
            <span className="font-semibold text-primary">${p.price}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
